import { useMemo } from 'react'

import { unparse } from 'papaparse'

import { CheckIcon, CriticalIcon, Flex, Link, Text } from '@buffer-mono/popcorn'

import { isError, isSuccess, isValid, type ListItem } from './helpers'

import styles from './StatusCounters.module.css'

export const StatusCounters = ({
  items,
  isCreatingPosts,
  isPostCreationComplete,
  selectedSaveAction,
}: {
  items: ListItem[]
  isCreatingPosts: boolean
  isPostCreationComplete: boolean
  selectedSaveAction: 'post' | 'draft'
}): JSX.Element => {
  const validItemCount = useMemo(() => {
    return items.filter((item) => isValid(item)).length
  }, [items])
  const successItemCount = useMemo(() => {
    return items.filter((item) => isSuccess(item)).length
  }, [items])
  const errorItemCount = useMemo(() => {
    return items.filter((item) => isError(item)).length
  }, [items])

  const validItemCountText = useMemo(() => {
    return `${validItemCount} valid ${selectedSaveAction}${
      validItemCount > 1 ? 's' : ''
    }`
  }, [validItemCount, selectedSaveAction])

  const successItemCountText = useMemo(() => {
    return `${successItemCount} ${selectedSaveAction}${
      successItemCount > 1 ? 's' : ''
    } ${selectedSaveAction === 'draft' ? 'saved' : 'scheduled'}${
      isCreatingPosts ? '...' : ''
    }`
  }, [successItemCount, isCreatingPosts, selectedSaveAction])

  const errorItemCountText = useMemo(() => {
    return `${errorItemCount} ${selectedSaveAction}${
      errorItemCount > 1 ? 's' : ''
    } with errors${isCreatingPosts ? '...' : ''}`
  }, [errorItemCount, isCreatingPosts, selectedSaveAction])

  // Construct a CSV of failed posts using papaparse with the following columns: Text, Image URL, Tags, Errors
  const shouldShowFailedPostsCsv = useMemo(() => {
    const hasErrors = errorItemCount > 0
    return hasErrors && isPostCreationComplete
  }, [errorItemCount, isPostCreationComplete])

  const failedPostsCsv = useMemo(() => {
    const failedItems = items.filter(isError)
    if (failedItems.length === 0 || !isPostCreationComplete) return ''

    const data = failedItems.map((item) => ({
      Text: item.data.text,
      'Image URL': item.data.media?.photo || '',
      Tags: item.data.tags.map((tag) => tag.name).join(','),
      Errors: item.errors?.join('|') || '',
    }))

    return unparse(data, {
      quotes: true,
    })
  }, [items, isPostCreationComplete])

  const onDownloadFailedPostsCsv = useMemo(
    () => (): void => {
      const blob = new Blob([failedPostsCsv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'failed-posts.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    [failedPostsCsv],
  )

  return (
    <Flex align="center" gap="lg" className={styles.statusCounters}>
      {isCreatingPosts || isPostCreationComplete ? (
        <Flex align="center" gap="xs">
          <CheckIcon color="success" />
          <Text>{successItemCountText}</Text>
        </Flex>
      ) : (
        <Flex align="center" gap="xs">
          <CheckIcon />
          <Text>{validItemCountText}</Text>
        </Flex>
      )}
      {errorItemCount > 0 && (
        <Flex align="center" gap="xs">
          <CriticalIcon color="critical" />
          <Text>
            {errorItemCountText}
            {shouldShowFailedPostsCsv && (
              <>
                .{' '}
                <Link as="button" onClick={onDownloadFailedPostsCsv}>
                  Download CSV with post errors
                </Link>
              </>
            )}
          </Text>
        </Flex>
      )}
    </Flex>
  )
}
